import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from '@/App';

// Context for trial expiration state
const TrialContext = createContext(null);

export const useTrialStatus = () => {
  const context = useContext(TrialContext);
  if (!context) {
    throw new Error('useTrialStatus must be used within a TrialProvider');
  }
  return context;
};

export const TrialProvider = ({ children }) => {
  const { user } = useAuth();

  const trialStatus = useMemo(() => {
    if (!user) {
      return {
        isTrialExpired: false,
        isTrialUser: false,
        trialExpiresAt: null,
        daysLeft: null,
        canPerformActions: true,
      };
    }

    const isTrialUser = user.plan === 'trial';
    const isTrialExpired = user.trial_expired === true;
    const trialExpiresAt = user.trial_expires_at;

    // Calculate days left
    let daysLeft = null;
    if (trialExpiresAt && !isTrialExpired) {
      const now = new Date().getTime();
      const expiry = new Date(trialExpiresAt).getTime();
      const difference = expiry - now;
      daysLeft = Math.max(0, Math.ceil(difference / (1000 * 60 * 60 * 24)));
    }

    return {
      isTrialExpired,
      isTrialUser,
      trialExpiresAt,
      daysLeft,
      // User can perform actions if NOT on expired trial
      canPerformActions: !isTrialExpired,
    };
  }, [user]);

  return (
    <TrialContext.Provider value={trialStatus}>
      {children}
    </TrialContext.Provider>
  );
};

export default TrialContext;
